import { ARTICLE_ARCHITECT_PAGE_ROUTE } from '@constants/routes';
import { ThirdPartyIntegrationType } from '@conversed/shared/src/central/types/third-party-integrations.types';
import { useTopdeskArticleGeneration } from '@hooks/article-architect/useTopdeskArticleGeneration';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { createContext, type ReactNode, useContext } from 'react';
import { useLocation } from 'react-router';
import { TopdeskGenerationEstimateModal } from './TopdeskGenerationEstimateModal';

type TopdeskArticleGenerationValue = ReturnType<
  typeof useTopdeskArticleGeneration
>;

const TopdeskArticleGenerationContext =
  createContext<TopdeskArticleGenerationValue | null>(null);

// * Reads the shared generation state. Returns null outside an active provider
// * (a non-TopDesk tenant, or a route outside the Article Architect group), so
// * a consumer that can render off the AA page must handle the null case.
export const useTopdeskArticleGenerationContext =
  (): TopdeskArticleGenerationValue | null =>
    useContext(TopdeskArticleGenerationContext);

// * Owns the generation hook and renders the estimate modal, so both survive
// * navigation between the AA page and the generation route. Mounted once above
// * the router, it connects the socket only while the run is reachable, not while
// * the user is anywhere else in the app.
const ActiveTopdeskArticleGenerationProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const generation = useTopdeskArticleGeneration();
  const { selectedCompany } = useCompanySelector();

  const handleConfirm = (): void => {
    if (selectedCompany?.id) {
      generation.confirmGeneration(selectedCompany.id);
    }
  };

  return (
    <TopdeskArticleGenerationContext.Provider value={generation}>
      {children}
      {generation.estimate && (
        <TopdeskGenerationEstimateModal
          estimate={generation.estimate}
          onConfirm={handleConfirm}
          onCancel={generation.cancelEstimate}
        />
      )}
    </TopdeskArticleGenerationContext.Provider>
  );
};

// * Mounts the generation state only while a TopDesk tenant sits within the
// * Article Architect route group. The path prefix keeps the same instance alive
// * across the AA page and the generation route, so a run started on the card is
// * still live on the route (and back). Leaving the group unmounts it; the hook's
// * backfill-run poll reconstructs an in-progress run on return.
export const TopdeskArticleGenerationProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { pathname } = useLocation();
  const { selectedCompany } = useCompanySelector();

  const isWithinArticleArchitect = pathname.startsWith(
    ARTICLE_ARCHITECT_PAGE_ROUTE,
  );
  const isTopdeskEnabled = (
    selectedCompany?.thirdPartyIntegrations ?? []
  ).includes(ThirdPartyIntegrationType.TopDesk);

  if (!isWithinArticleArchitect || !isTopdeskEnabled) {
    return <>{children}</>;
  }

  return (
    <ActiveTopdeskArticleGenerationProvider>
      {children}
    </ActiveTopdeskArticleGenerationProvider>
  );
};
